<template>
  <div class="letter-arrange">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <div class="game-container">
        <div class="targets">
          <div v-for="(letter, index) in currentCategory" :key="index" :class="[
            'target-letter',
            {
              'correct-drop': isCorrectDrop[letter],
              'hidden-letter':
                hiddenTargetLetters[letter] && !isCorrectDrop[letter],
            },
          ]" @dragover.prevent @drop="handleDrop($event, letter)" @click="speakOutLoud(letter)">
            {{
              isCorrectDrop[letter] || !hiddenTargetLetters[letter]
                ? letter
                : " "
            }}
          </div>
        </div>
        <div class="random-grid">
          <div v-for="(letterObj) in letters" :key="letterObj.id" class="letter-draggable" draggable="true"
            :style="calculateLetterStyle(letterObj)" @dragstart="handleDrag($event, letterObj)"
            @click="speakOutLoud(letterObj.letter)" @touchstart="handleTouchStart($event, letterObj)"
            @touchmove="handleTouchMove($event)" @touchend="handleTouchEnd($event)">
            {{ letterObj.letter }}
          </div>
        </div>
      </div>
      <p v-if="message" class="message">
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin.js";

import LevelComplete from "../LevelComplete.vue";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    selectedCategory: {
      type: String,
      default: "vowels",
    },
    difficulty: {
      type: Number,
      default: 1,
      validator: (value) => [1, 2, 3].includes(value),
    },
  },
  data() {
    return {
      categories: {
        vowels: ["A", "E", "I", "O", "U"],
        alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
        numbers: [
          ["1", "2", "3", "4", "5"],
          ["6", "7", "8", "9", "10"],
        ],
      },
      letters: [],
      message: "",
      isCorrectDrop: {},
      selectedVoice: null,
      isGameComplete: false,
      touchOffsetX: 0,
      touchOffsetY: 0,
      draggedLetter: null,
      draggedStyle: {},
      hiddenTargetLetters: {},
      letterIdCounter: 0,
      currentGroupIndex: 0,
    };
  },
  computed: {
    currentCategory() {
      if (this.selectedCategory === "numbers") {
        return this.categories.numbers[this.currentGroupIndex];
      }
      return this.categories[this.selectedCategory];
    },
  },
  mounted() {
    this.setupGame();
    this.setupVoices();

    // Handle window resize to rearrange letters on mobile
    window.addEventListener("resize", this.arrangeDraggableLetters);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.arrangeDraggableLetters);
  },
  methods: {
    setupGame() {
      // Reset the letters array
      this.letters = [];

      // Initialize isCorrectDrop for each letter
      this.isCorrectDrop = {};
      this.currentCategory.forEach((letter) => {
        this.isCorrectDrop[letter] = false;
      });

      // Set up hiddenTargetLetters based on difficulty
      this.setupHiddenTargetLetters();

      // Generate the draggable letters
      this.setupDraggableLetters();

      this.message = "";
      this.isGameComplete = false;
    },
    setupHiddenTargetLetters() {
      // Set up hiddenTargetLetters based on difficulty
      this.hiddenTargetLetters = {};

      const totalLetters = this.currentCategory.length;

      let lettersToHide = 0;
      if (this.difficulty === 1) {
        lettersToHide = 0;
      } else if (this.difficulty === 2) {
        lettersToHide = Math.floor(totalLetters / 2); // Hide half the letters
      } else if (this.difficulty === 3) {
        lettersToHide = Math.floor((2 * totalLetters) / 3); // Hide 2/3 of the letters
      }

      const lettersCopy = [...this.currentCategory];
      // Shuffle the letters
      this.shuffleArray(lettersCopy);
      const lettersToHideArray = lettersCopy.slice(0, lettersToHide);

      lettersToHideArray.forEach((letter) => {
        this.hiddenTargetLetters[letter] = true;
      });

      // For letters not hidden, set to false
      this.currentCategory.forEach((letter) => {
        if (this.hiddenTargetLetters[letter] === undefined) {
          this.hiddenTargetLetters[letter] = false;
        }
      });
    },
    setupDraggableLetters() {
      // Include each letter from currentCategory exactly once
      const lettersArray = this.shuffleArray([...this.currentCategory]);

      // Initialize letters array
      this.letters = lettersArray.map((letter) => {
        const id = this.letterIdCounter++;
        return {
          id: id,
          letter: letter,
        };
      });
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    handleDrag(event, letterObj) {
      event.dataTransfer.setData(
        "text/plain",
        JSON.stringify({ id: letterObj.id })
      );
      this.draggedLetter = letterObj;
    },
    handleDrop(event, targetLetter) {
      const { id } = JSON.parse(event.dataTransfer.getData("text/plain"));
      const droppedLetterObj = this.letters.find((letter) => letter.id === id);
      if (droppedLetterObj && droppedLetterObj.letter === targetLetter) {
        // Correct drop
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.speakOutLoud(`${targetLetter}`);
        this.message = "✅";
        this.isCorrectDrop[targetLetter] = true;

        // Remove the letter from the draggable letters
        const index = this.letters.findIndex((letter) => letter.id === id);
        if (index !== -1) {
          this.letters.splice(index, 1);
        }

        if (Object.values(this.isCorrectDrop).every(Boolean)) {
          if (
            this.selectedCategory === "numbers" &&
            this.currentGroupIndex < this.categories.numbers.length - 1
          ) {
            // Move to next group
            this.currentGroupIndex++;
            this.setupGame();
          } else {
            this.isGameComplete = true;
            this.playSound(
              "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
            );
            this.speakOutLoud("Great Job!");

            setTimeout(() => {
              this.$router.push(
                `/${this.selectedCategory === "numbers" ? "numbers" : "letters"
                }`
              );
            }, 4000);
          }
        }
      } else {
        this.message = "❌";
        setTimeout(() => {
          this.message = "";
        }, 3000);
      }
      this.draggedLetter = null;
      this.draggedStyle = {};
    },
    handleTouchStart(event, letterObj) {
      event.preventDefault(); // Prevent default touch behavior
      this.draggedLetter = letterObj;

      const touch = event.touches[0];
      const letterElement = event.target;

      // Get the bounding rectangle of the letter element
      const rect = letterElement.getBoundingClientRect();

      // Calculate the offset between the touch point and the element's top-left corner
      this.touchOffsetX = touch.clientX - rect.left;
      this.touchOffsetY = touch.clientY - rect.top;
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      if (!this.draggedLetter) return;

      const touch = event.touches[0];
      this.updateDraggedStyle(touch.clientX, touch.clientY);
    },
    handleTouchEnd(event) {
      if (!this.draggedLetter) return;

      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;

      let droppedOnTarget = false;

      const dropZones = this.$el.querySelectorAll(".target-letter");
      dropZones.forEach((dropZone, index) => {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          this.handleDrop(
            {
              dataTransfer: {
                getData: () => JSON.stringify({ id: this.draggedLetter.id }),
              },
            },
            this.currentCategory[index]
          );
          droppedOnTarget = true;
        }
      });

      if (!droppedOnTarget) {
        // Reset the position of the letter
        this.draggedStyle = {};
      }

      this.draggedLetter = null;
      this.draggedStyle = {};
    },
    updateDraggedStyle(clientX, clientY) {
      const x = clientX - this.touchOffsetX;
      const y = clientY - this.touchOffsetY;

      this.draggedStyle = {
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
        zIndex: 1000,
      };
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    calculateLetterStyle(letterObj) {
      if (
        this.draggedLetter &&
        this.draggedLetter.id === letterObj.id &&
        Object.keys(this.draggedStyle).length
      ) {
        return this.draggedStyle;
      } else {
        return {};
      }
    },
  },
};
</script>

<style scoped>
.game-container {}

.targets {
  display: flex;
  justify-content: center;
  /* Center the flex items horizontally */
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  margin-bottom: 20px;
}

.random-grid {
  display: flex;
  justify-content: center;
  /* Center the flex items horizontally */
  flex-direction: row;
  /* Letters stack horizontally on desktop */
  align-items: center;
  gap: 1rem;
}

.target-letter {
  width: 100px;
  height: 100px;
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  background-color: lightgray;
  border-radius: 50px;
  border: 8px dashed gray;
  cursor: pointer;
  transition: all 0.3s;
  line-height: 100px;
}

.correct-drop {
  background-color: lightgreen;
  transform: scale(1.2);
  border: 8px green solid;
}

.hidden-letter {
  width: 100px;
  height: 100px;
}

.letter-draggable {
  cursor: grab;
  border: 2px solid black;
  border-radius: 50px;
  width: 100px;
  height: 100px;
  font-size: 3rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
  line-height: 100px;
}

.letter-draggable:hover {
  transform: scale(1.1);
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}

/* Adjust layout and sizes for mobile and tablets */
@media (max-width: 768px) {
  .game-container {
    display: flex;
    /* Enable flex layout */
    justify-content: center;
    /* Center the flex items horizontally */
    align-items: flex-start;
    /* Align items to the top (optional) */
    gap: 2rem;
  }

  .targets,
  .random-grid {
    display: flex;
    flex-direction: column;
    /* Stack letters vertically within each section */
    max-width: 100px;
  }

  .target-letter,
  .letter-draggable {
    width: 80px;
    height: 80px;
    font-size: 3rem;
    line-height: 70px;
  }
}
</style>